import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Icons } from '../utils/icons';
import api from '../utils/api';
import { fontStack } from '../styles/shared';

// ─── Constants ──────────────────────────────────────────────────────────────
const PERIODS = [
    { value: '7d', label: '7D', days: 7 },
    { value: '30d', label: '30D', days: 30 },
    { value: '90d', label: '90D', days: 90 },
];
const fmtUsd = (v) => `$${(v || 0).toFixed(2)}`;
const fmtCompact = (v) => {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
    return `$${v.toFixed(2)}`;
};
const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const purchaseLabel = (t) => {
    if (t === 'subscription') return 'Subscription';
    if (t === 'ppv') return 'Message';
    if (t === 'tip') return 'Tip';
    return 'Spin Purchase';
};
const purchaseColor = (t) => {
    if (t === 'subscription') return '#10b981';
    if (t === 'ppv') return '#f59e0b';
    if (t === 'tip') return '#8b5cf6';
    return '#2F6BFF';
};

// ─── Component ──────────────────────────────────────────────────────────────
export default function OverviewTab({ wheels = [], username, onNavigate, isMobile }) {
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');
    const [earnings, setEarnings] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [dailyEarnings, setDailyEarnings] = useState([]);
    const [subscriptionEarnings, setSubscriptionEarnings] = useState(null);
    const [totalEarnedAllUsd, setTotalEarnedAllUsd] = useState(0);
    const [starsPerUsd, setStarsPerUsd] = useState(77);
    const [chartTooltip, setChartTooltip] = useState(null);
    const chartContainerRef = useRef(null);

    const s2u = useCallback((stars) => (stars || 0) / starsPerUsd, [starsPerUsd]);

    // ─── Data Loading ───────────────────────────────────────────────────────
    useEffect(() => {
        loadEarnings();
    }, []);

    useEffect(() => {
        const p = PERIODS.find(p => p.value === period);
        loadDailyEarnings((p?.days ?? 30) * 2); // fetch 2x for prev period comparison
    }, [period]);

    const loadEarnings = async () => {
        try {
            const res = await api.post('/api/dashboard/earnings', { token: localStorage.getItem('token') });
            setEarnings(res.data.earnings);
            setTransactions(res.data.transactions || []);
            setStarsPerUsd(res.data.stars_per_usd || 77);
if (res.data.subscription_earnings) setSubscriptionEarnings(res.data.subscription_earnings);
            if (res.data.total_earned_all_usd != null) setTotalEarnedAllUsd(res.data.total_earned_all_usd);
        } catch (e) { /* silent */ }
        finally { setLoading(false); }
    };

    const loadDailyEarnings = async (days = 30) => {
        try {
            const res = await api.get(`/api/dashboard/daily-earnings?days=${days}`);
            setDailyEarnings(res.data.daily_earnings || []);
        } catch (e) { /* silent */ }
    };

    // ─── Computed Data ──────────────────────────────────────────────────────
    const periodDays = PERIODS.find(p => p.value === period)?.days || 30;

    const chartData = useMemo(() => {
        const result = [];
        const mkVal = (d) => s2u(d.earned || 0) + (d.crypto_usd || 0) * 0.80;
        const lookup = {};
        dailyEarnings.forEach(d => { lookup[d.date] = d; });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        for (let i = periodDays - 1; i >= 0; i--) {
            const dt = new Date(today); dt.setDate(dt.getDate() - i);
            const dateStr = localDateStr(dt);
            const d = lookup[dateStr] || { date: dateStr, earned: 0, crypto_usd: 0, transactions: 0 };
            result.push({ ...d, date: dateStr, earned_usd: mkVal(d) });
        }
        return result;
    }, [dailyEarnings, periodDays, s2u]);

    const periodRevenue = useMemo(() => chartData.reduce((sum, d) => sum + d.earned_usd, 0), [chartData]);

    const prevPeriodRevenue = useMemo(() => {
        const mkVal = (d) => s2u(d.earned || 0) + (d.crypto_usd || 0) * 0.80;
        const lookup = {};
        dailyEarnings.forEach(d => { lookup[d.date] = d; });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        let sum = 0;
        for (let i = periodDays * 2 - 1; i >= periodDays; i--) {
            const dt = new Date(today); dt.setDate(dt.getDate() - i);
            const dateStr = localDateStr(dt);
            const d = lookup[dateStr];
            if (d) sum += mkVal(d);
        }
        return sum;
    }, [dailyEarnings, periodDays, s2u]);

    const revenueChange = prevPeriodRevenue > 0
        ? ((periodRevenue - prevPeriodRevenue) / prevPeriodRevenue * 100)
        : (periodRevenue > 0 ? 100 : 0);

    const activeSubscribers = subscriptionEarnings?.active_subscribers || 0;
    const channelCount = wheels.length;

    // Compute per-channel earnings from transactions (backend only sends breakdown for chatters)
    const channelEarnings = useMemo(() => {
        const byChannel = {};
        transactions.forEach(t => {
            const ch = t.channel_username || 'Unknown';
            if (!byChannel[ch]) byChannel[ch] = { earned_usd: 0, count: 0 };
            const isUsd = t.payment_method === 'crypto' || t.payment_method === 'card';
            byChannel[ch].earned_usd += isUsd ? parseFloat(t.amount_usd || 0) * 0.80 : (t.admin_share || 0) / starsPerUsd;
            byChannel[ch].count += 1;
        });
        return byChannel;
    }, [transactions, starsPerUsd]);

    // ─── Render ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 40px' }}>
                <img src="/logo-light.webp" alt="Loading" style={{ height: '28px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
        );
    }

    return (
        <div style={{ fontFamily: fontStack, maxWidth: '1100px', margin: '0 auto' }}>
            {/* ─── Header ─────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                        Hello, @{username}
                    </h1>
                    <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>Welcome back to your dashboard.</p>
                </div>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--surface-hover)', borderRadius: '10px', padding: '3px' }}>
                    {PERIODS.map(p => (
                        <button key={p.value} onClick={() => setPeriod(p.value)}
                            style={{
                                padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                fontSize: '13px', fontWeight: 600, fontFamily: fontStack,
                                background: period === p.value ? '#fff' : 'transparent',
                                color: period === p.value ? 'var(--text)' : 'var(--text-muted)',
                                boxShadow: period === p.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.15s ease'
                            }}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ─── Stat Cards ─────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '10px' : '16px', marginBottom: '24px' }}>
                <StatCard
                    label={`Revenue (${PERIODS.find(p => p.value === period)?.label})`}
                    value={fmtCompact(periodRevenue)}
                    change={revenueChange}
                    icon={<Icons.DollarSign />}
                    accent="#10b981"
                />
                <StatCard
                    label="Active Subscribers"
                    value={activeSubscribers.toLocaleString()}
                    icon={<Icons.Users />}
                    accent="#2F6BFF"
                />
                <StatCard
                    label="Channels"
                    value={channelCount.toString()}
                    icon={<Icons.Grid />}
                    accent="#8b5cf6"
                />
                <StatCard
                    label="Lifetime Earned"
                    value={fmtCompact(totalEarnedAllUsd)}
                    icon={<Icons.Wallet />}
                    accent="#f59e0b"
                />
            </div>

            {/* ─── Chart + Top Channels ───────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: '16px', marginBottom: '24px' }}>
                {/* Revenue Chart */}
                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Revenue Overview</h3>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {PERIODS.find(p => p.value === period)?.label}
                        </span>
                    </div>
                    {chartData.length > 0 && (() => {
                        const maxEarned = Math.max(...chartData.map(d => d.earned_usd), 0.01);
                        const chartWidth = 700;
                        const chartHeight = 220;
                        const padding = { top: 20, right: 16, bottom: 32, left: 50 };
                        const innerWidth = chartWidth - padding.left - padding.right;
                        const innerHeight = chartHeight - padding.top - padding.bottom;
                        const yMax = padding.top + innerHeight;

                        const points = chartData.map((d, i) => ({
                            x: padding.left + (i / (chartData.length - 1 || 1)) * innerWidth,
                            y: yMax - (d.earned_usd / maxEarned) * innerHeight,
                            ...d
                        }));

                        const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
                        const areaPath = linePath + ` L${points[points.length - 1].x},${yMax} L${points[0].x},${yMax} Z`;

                        return (
                            <div ref={chartContainerRef} style={{ position: 'relative', width: '100%', overflow: 'hidden' }} onMouseLeave={() => setChartTooltip(null)}>
                                <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ width: '100%', height: 'auto', display: 'block' }}
                                    onMouseMove={e => {
                                        const container = chartContainerRef.current;
                                        if (!container) return;
                                        const rect = container.getBoundingClientRect();
                                        const svgRect = e.currentTarget.getBoundingClientRect();
                                        const scaleX = svgRect.width / chartWidth;
                                        const mouseX = (e.clientX - svgRect.left) / scaleX;
                                        let closest = points[0], minDist = Infinity;
                                        points.forEach(p => { const d = Math.abs(p.x - mouseX); if (d < minDist) { minDist = d; closest = p; } });
                                        setChartTooltip({
                                            ...closest,
                                            svgX: closest.x,
                                            x: (closest.x * scaleX),
                                            y: (closest.y * (svgRect.height / chartHeight)),
                                            containerWidth: rect.width
                                        });
                                    }}>
                                    {/* Grid lines */}
                                    {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
                                        const y = yMax - frac * innerHeight;
                                        const val = frac * maxEarned;
                                        return (
                                            <g key={i}>
                                                <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="var(--border)" strokeDasharray="3 3" opacity="0.5" />
                                                <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">{val < 1 ? '' : `$${Math.round(val)}`}</text>
                                            </g>
                                        );
                                    })}
                                    {/* Area fill */}
                                    <path d={areaPath} fill="url(#areaGrad)" />
                                    <defs>
                                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#2F6BFF" stopOpacity="0.15" />
                                            <stop offset="100%" stopColor="#2F6BFF" stopOpacity="0.01" />
                                        </linearGradient>
                                    </defs>
                                    {/* Line */}
                                    <path d={linePath} fill="none" stroke="#2F6BFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    {/* Hover line */}
                                    {chartTooltip && chartTooltip.svgX != null && (
                                        <line x1={chartTooltip.svgX} y1={padding.top} x2={chartTooltip.svgX} y2={yMax} stroke="#2F6BFF" strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
                                    )}
                                    {/* Hover dot */}
                                    {chartTooltip && points.map((pt, i) => {
                                        const isHovered = Math.abs((chartTooltip.svgX || 0) - pt.x) < 2;
                                        if (!isHovered) return null;
                                        return <circle key={i} cx={pt.x} cy={pt.y} r="5" fill="#2F6BFF" stroke="#fff" strokeWidth="2.5" />;
                                    })}
                                    {/* X axis labels */}
                                    {(() => {
                                        const step = chartData.length <= 10 ? 1 : chartData.length <= 30 ? 3 : 7;
                                        return chartData.map((d, i) => {
                                            if (i % step !== 0 && i !== chartData.length - 1) return null;
                                            const x = padding.left + (i / (chartData.length - 1 || 1)) * innerWidth;
                                            const dt = new Date(d.date + 'T00:00:00');
                                            const label = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                            return <text key={i} x={x} y={yMax + 18} textAnchor="middle" fontSize="10" fill="var(--text-muted)">{label}</text>;
                                        });
                                    })()}
                                </svg>
                                {/* Tooltip */}
                                {chartTooltip && (() => {
                                    const tw = 120, th = 60, gap = 12;
                                    const cw = chartTooltip.containerWidth || 500;
                                    let top = chartTooltip.y - th - gap;
                                    if (top < 0) top = chartTooltip.y + gap + 10;
                                    let left = chartTooltip.x + gap + tw < cw - 4 ? chartTooltip.x + gap : chartTooltip.x - gap - tw;
                                    if (left < 4) left = 4;
                                    return (
                                        <div style={{ position: 'absolute', top, left, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', pointerEvents: 'none', zIndex: 10 }}>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>{new Date(chartTooltip.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#2F6BFF' }}>${chartTooltip.earned_usd?.toFixed(2)}</div>
                                            <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '3px' }}>{chartTooltip.transactions} sale{chartTooltip.transactions !== 1 ? 's' : ''}</div>
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })()}
                </div>

                {/* Top Channels */}
                <div style={cardStyle}>
                    <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Top Channels</h3>
                    {wheels.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px 16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            <Icons.Grid />
                            <p style={{ marginTop: '8px' }}>No channels yet</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {[...wheels].sort((a, b) => (channelEarnings[b.channel_username]?.earned_usd || 0) - (channelEarnings[a.channel_username]?.earned_usd || 0)).map((w, idx) => {
                                const ch = channelEarnings[w.channel_username];
                                const earned = ch?.earned_usd || 0;
                                const txCount = ch?.count || 0;
                                const maxEarned = Math.max(...wheels.map(ww => {
                                    return channelEarnings[ww.channel_username]?.earned_usd || 0;
                                }), 0.01);
                                const barPct = Math.max(5, (earned / maxEarned) * 100);
                                return (
                                    <div key={w.channel_username} style={{
                                        padding: '12px', borderRadius: '10px', position: 'relative', overflow: 'hidden',
                                        border: '1px solid var(--border)', transition: 'all 0.15s ease', cursor: 'pointer'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(47,107,255,0.08)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                                        {/* Progress bar background */}
                                        <div style={{
                                            position: 'absolute', top: 0, left: 0, bottom: 0,
                                            width: `${barPct}%`, background: earned > 0 ? 'rgba(47,107,255,0.04)' : 'transparent',
                                            transition: 'width 0.4s ease', borderRadius: '10px'
                                        }} />
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '38px', height: '38px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0,
                                                background: `linear-gradient(135deg, ${['#2F6BFF', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444'][idx % 5]}, ${['#1D5AE6', '#7c3aed', '#d97706', '#059669', '#dc2626'][idx % 5]})`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#fff', fontSize: '15px', fontWeight: 700
                                            }}>
                                                {(w.title || w.channel_username || 'C')[0].toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {w.title || w.channel_username}
                                                </p>
                                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                                                    {txCount} sale{txCount !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <p style={{ fontSize: '14px', fontWeight: 700, color: earned > 0 ? 'var(--text)' : 'var(--text-muted)', margin: 0 }}>
                                                    {earned > 0 ? fmtUsd(earned) : '$0.00'}
                                                </p>
                                                <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                                                    earned
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Recent Payments ─────────────────────────────────────────── */}
            <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Recent Payments</h3>
                    <button onClick={() => onNavigate?.('/dashboard/payments')}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: fontStack, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        View all <span style={{ fontSize: '16px' }}>&rsaquo;</span>
                    </button>
                </div>
                {transactions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px 16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                        No payments yet
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Customer</th>
                                    <th style={thStyle}>Type</th>
                                    <th style={thStyle}>Channel</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.slice(0, 5).map((tx, i) => {
                                    const name = tx.buyer_name || tx.buyer_username || `User ${tx.user_id}`;
                                    const type = purchaseLabel(tx.purchase_type);
                                    const color = purchaseColor(tx.purchase_type);
                                    const isUsd = tx.payment_method === 'crypto' || tx.payment_method === 'card';
                                    const amount = isUsd
                                        ? parseFloat(tx.amount_usd || 0)
                                        : (tx.amount_stars || 0) / starsPerUsd;
                                    const timeStr = tx.created_at ? new Date(tx.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
                                    const channel = tx.channel_username || tx.admin_username || '—';
                                    return (
                                        <tr key={tx.id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                                            <td style={tdStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{
                                                        width: '30px', height: '30px', borderRadius: '50%',
                                                        background: `${color}15`, color: color,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '12px', flexShrink: 0
                                                    }}>
                                                        {(name[0] || '?').toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{name}</div>
                                                        {timeStr && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{timeStr}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    padding: '3px 8px', borderRadius: '6px',
                                                    background: `${color}12`, color: color,
                                                    fontSize: '11px', fontWeight: 600
                                                }}>
                                                    {type}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>@{channel}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>
                                                {fmtUsd(amount)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <span style={{
                                                    padding: '3px 8px', borderRadius: '6px',
                                                    fontSize: '11px', fontWeight: 600,
                                                    background: tx.status === 'completed' ? 'rgba(16,185,129,0.1)' : 'rgba(249,115,22,0.1)',
                                                    color: tx.status === 'completed' ? '#10b981' : '#f97316'
                                                }}>
                                                    {tx.status === 'completed' ? 'Completed' : 'Pending'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ label, value, change, icon, accent }) {
    return (
        <div style={{
            ...cardStyle,
            transition: 'all 0.2s ease',
            cursor: 'default'
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
                <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: `${accent}12`, color: accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px'
                }}>
                    {icon}
                </div>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>{value}</div>
            {change != null && (
                <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    marginTop: '8px', fontSize: '12px', fontWeight: 600,
                    color: change >= 0 ? '#10b981' : '#ef4444'
                }}>
                    <span>{change >= 0 ? '↑' : '↓'}</span>
                    <span>{Math.abs(change).toFixed(1)}%</span>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>vs prev period</span>
                </div>
            )}
        </div>
    );
}

// ─── Shared Styles ──────────────────────────────────────────────────────────
const cardStyle = {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border)',
};

const tdStyle = {
    padding: '12px',
    verticalAlign: 'middle',
};
